// Mirrors the DTOs in com.mayam.buacam.domain.dto. Field names and shapes
// must stay in lock-step with the API — if the Java record changes, this
// changes too.

export type Species = "moose" | "roe-deer" | "wild-boar" | "other";
export type AnalysisState = "pending" | "done";

export interface Analysis {
  moose: boolean;
  roeDeer: boolean;
  wildBoar: boolean;
  bird: boolean;
  human: boolean;
  state: AnalysisState;
  analyzedAt: string | null;
  modelVersion: string | null;
  speciesOverride: Species | null;
  /** Username that set the manual override; null when AI-tagged or unset. */
  speciesOverrideBy: string | null;
  /** ISO timestamp of the manual override; null when AI-tagged or unset. */
  speciesOverrideAt: string | null;
}

export interface Shot {
  imageId: string;
  cameraId: string;
  filename: string;
  lat: number | null;
  lon: number | null;
  capturedAt: string;
  uploadedAt: string;
  contentType: string;
  sizeBytes: number;
  hidden: boolean;
  analysis: Analysis;
}

export interface Camera {
  cameraId: string;
  name: string;
  lat: number | null;
  lon: number | null;
  firstSeen: string;
  lastSeen: string;
}

export interface ImagesPage {
  items: Shot[];
  nextCursor: string | null;
}

export interface ImagesQuery {
  imageId?: string;
  cameraId?: string;
  from?: string;
  to?: string;
  moose?: boolean;
  roeDeer?: boolean;
  wildBoar?: boolean;
  bird?: boolean;
  human?: boolean;
  analysisState?: "pending" | "done" | "any";
  limit?: number;
  cursor?: string;
}

export interface Comment {
  commentId: number;
  username: string;
  body: string;
  createdAt: string;
  /** Equal to createdAt when never edited — unedited is not a null. */
  updatedAt: string;
  /**
   * Whether you wrote this one. Only its author may change it: an admin can
   * delete a whole Post, but nobody edits somebody else's words. Always false
   * for a comment on an Image, which has no edit or delete endpoint.
   */
  canEdit: boolean;
}

export interface LikeStatus {
  count: number;
  likedByMe: boolean;
}

// ---- Schedule -------------------------------------------------------------
// The team's plan of Events. Distinct from the Calendar, which is about
// photographs — see CONTEXT.md. Nothing here is reachable below level 2.

/** Only two answers exist. Pending is the absence of a Response, not a value. */
export type Answer = "yes" | "no";

/**
 * Someone who can be invited, and someone who was. The same shape serves the
 * team roster the create form ticks through and the invited set on an Event —
 * they are the same question asked at two moments. No email: the picker needs
 * to tell two people apart, which the name does.
 */
export interface TeamMember {
  userId: string;
  name: string;
}

export interface ScheduleResponseView {
  userId: string;
  /** Display name, falling back to the email server-side. */
  name: string;
  answer: Answer;
  comment: string | null;
  respondedAt: string;
}

export interface ScheduleEvent {
  eventId: string;
  /** Local calendar day, YYYY-MM-DD. An Event is always exactly one day. */
  date: string;
  /** Gathering time as HH:mm(:ss), or null when the Event has none. */
  startTime: string | null;
  kind: string;
  title: string;
  description: string | null;
  locationText: string | null;
  createdBy: string;
  createdById: string;
  cancelled: boolean;
  cancelledAt: string | null;
  cancelReason: string | null;
  yesCount: number;
  noCount: number;
  /** Invited people who have not answered. Derived server-side per request. */
  pendingCount: number;
  /**
   * Everyone asked to this Event. An Event is addressed — you only ever see one
   * you were invited to, created, or (as an admin) any at all.
   */
  invitees: TeamMember[];
  responses: ScheduleResponseView[];
  /** Your own answer, or null when you have not answered. */
  myResponse: ScheduleResponseView | null;
  /** True when you may edit or cancel — you created it, or you are an admin. */
  canEdit: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Body for creating or editing an Event. Mirrors ScheduleEventRequest. */
export interface ScheduleEventInput {
  date: string;
  startTime: string | null;
  kind: string;
  title: string;
  description: string | null;
  locationText: string | null;
  /**
   * Who to invite. Null invites the whole team — the server's default, and what
   * every Event meant before Events were addressed. An empty array is refused
   * rather than read as "everyone".
   *
   * <p>On edit this replaces the invited set: people added are mailed, people
   * removed lose their answer.
   */
  inviteeIds: string[] | null;
}

/** An Event kind already in use, with how many Events use it. */
export interface ScheduleKind {
  kind: string;
  count: number;
}

// ---- Users ----------------------------------------------------------------

export interface AdminUser {
  userId: string;
  /** Immutable after creation (ADR-0002) — the admin table renders it read-only. */
  email: string;
  displayName: string | null;
  accessLevel: number;
  active: boolean;
  /** False for an SSO-only user: their row offers "set" rather than "reset". */
  hasPassword: boolean;
  createdAt: string;
  updatedAt: string;
}

export type NotificationType = "image.uploaded" | "image.analyzed";

export interface NotificationEvent {
  type: NotificationType;
  imageId: string;
  cameraId: string;
  timestamp: string;
}

/**
 * Derive the design's chip value from the AI booleans, falling back to the
 * manual override if set. Returns null for "untagged" (analysis pending or
 * truly nothing detected).
 */
// Keys for the multi-select analysis-type filter. Mirror the chip vocabulary
// so toggling a chip surfaces exactly the cards whose chip already reads
// that label. "other" covers the chip's "Other" (bird/human/override) and
// done-but-truly-untagged frames.
export type SpeciesFilterKey = "moose" | "roe-deer" | "wild-boar" | "other";

export function matchesSpeciesFilter(
  a: Analysis,
  selected: ReadonlySet<SpeciesFilterKey>,
): boolean {
  // No selection ⇒ no filtering. The grid shows every frame the camera
  // filter and time range already let through.
  if (selected.size === 0) return true;
  const sp = deriveSpecies(a);
  if (sp === "moose" && selected.has("moose")) return true;
  if (sp === "roe-deer" && selected.has("roe-deer")) return true;
  if (sp === "wild-boar" && selected.has("wild-boar")) return true;
  if (selected.has("other")) {
    if (sp === "other") return true;
    // Truly nothing detected (analysis done, all five booleans false, no
    // override) — chip renders as "Untagged" but the user has no other
    // bucket to put it in, so it lives under "Other".
    if (sp === null && a.state === "done") return true;
  }
  return false;
}

export function deriveSpecies(a: Analysis): Species | null {
  if (a.speciesOverride) return a.speciesOverride;
  return aiSpecies(a);
}

/**
 * The species the AI model derived, ignoring any manual override. Used to show
 * the original tag in the "edited manually" tooltip. Returns null when analysis
 * is still pending or nothing was detected.
 */
export function aiSpecies(a: Analysis): Species | null {
  if (a.state !== "done") return null;
  // Human outranks the wildlife booleans — people on trail cams are usually
  // the signal you care most about, and the Florence-2 model frequently
  // hallucinates a moose/roe-deer alongside any human it detects.
  if (a.human) return "other";
  if (a.moose) return "moose";
  if (a.roeDeer) return "roe-deer";
  if (a.wildBoar) return "wild-boar";
  if (a.bird) return "other";
  return null;
}

/** True when a human has manually corrected the tag (regardless of provenance). */
export function isManuallyTagged(a: Analysis): boolean {
  return a.speciesOverride != null;
}

// ---- Forum ----------------------------------------------------------------
// The hunting team's own posts. Distinct from everything else here, which is
// about the cameras — a Post is a person talking to the team. Nothing in this
// section is reachable below level 2, reading included.

/**
 * How the feed is ordered. "latest" is newest-written; "updated" is
 * most-recently-active, where an edit or a comment both count.
 */
export type ForumSort = "latest" | "updated";

/** One image on a Post. The content path is built client-side from the id. */
export interface ForumImage {
  imageId: string;
  contentType: string;
  sizeBytes: number;
}

export interface ForumPost {
  postId: string;
  title: string;
  body: string | null;
  /** Display name, falling back to the email server-side. */
  author: string;
  authorId: string;
  images: ForumImage[];
  likeCount: number;
  likedByMe: boolean;
  /**
   * How many comments the server counted. The thread itself is loaded only when
   * you open it — shipping every thread in the feed would make the page cost
   * grow with the conversation.
   */
  commentCount: number;
  /** True when you may edit or delete — you wrote it, or you are an admin. */
  canEdit: boolean;
  createdAt: string;
  updatedAt: string;
  /**
   * When anybody last touched this Post — its own edit, or its newest comment,
   * whichever is later. Derived server-side. Shown instead of the post time when
   * the feed is sorted by activity, so a March post pulled to the top by a reply
   * this morning does not read as a sorting bug.
   */
  lastActivityAt: string;
}

// ---- Documents ------------------------------------------------------------
// The hunting team's shared files, in Folders. The third thing here that is not
// about the cameras, after the Schedule and the Forum — and distinct from the
// Forum, which is a conversation and scrolls away. A lease agreement has to be
// findable next season. Level 2 and above, reading included.

/**
 * One Folder. `parentId` null is a top-level Folder — there is no root row, so
 * the root is an absence rather than an id.
 */
export interface Folder {
  folderId: string;
  parentId: string | null;
  name: string;
  /** Display name of whoever created it, falling back to the email. */
  createdBy: string;
  /** Documents directly in it. Not recursive — what deleting it would be blocked by. */
  documentCount: number;
  /** Folders directly inside it. */
  folderCount: number;
  createdAt: string;
}

/**
 * How the Google Drive copy of a Document is going.
 *
 * "off" is the state on a host with no Drive credentials, and it is deliberately
 * not "pending": nothing is waiting to happen, and a column of pending rows that
 * never resolve is a status the team would learn to ignore.
 */
export type DriveStatus = "off" | "pending" | "copied" | "failed";

export interface TeamDocument {
  documentId: string;
  /** Null for the root, matching Folder.parentId. */
  folderId: string | null;
  filename: string;
  contentType: string;
  sizeBytes: number;
  /**
   * Whether `/thumbnail` will answer with bytes. False for everything that is
   * not a raster image the server could decode — the row shows a type icon
   * instead, and asking for a thumbnail that is not there would be a 404 per row
   * on every render.
   */
  hasThumbnail: boolean;
  /** Display name of the uploader, falling back to the email. */
  uploadedBy: string;
  createdAt: string;
  driveStatus: DriveStatus;
  /** Why the last copy failed. Null when it has not, and null always when off. */
  driveError: string | null;
  /** True when you may delete it — you uploaded it, or you are an admin. */
  canDelete: boolean;
}
